/**
 * Theme Context
 *
 * Provides light/dark theme switching functionality.
 * Theme is persisted to localStorage and applied via data-theme attribute.
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const THEME_STORAGE_KEY = 'kh-sponsor-theme';
const THEME_ATTRIBUTE = 'data-theme';

export const THEMES = {
  LIGHT: 'light',
  DARK: 'dark',
};

const ThemeContext = createContext(undefined);

/**
 * Get initial theme from localStorage or system preference
 */
const getInitialTheme = () => {
  // Check localStorage first
  const storedTheme = localStorage.getItem(THEME_STORAGE_KEY);
  if (storedTheme && Object.values(THEMES).includes(storedTheme)) {
    return storedTheme;
  }

  // Fall back to system preference
  if (typeof window !== 'undefined' && window.matchMedia) {
    if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return THEMES.DARK;
    }
  }

  // Default to light
  return THEMES.LIGHT;
};

/**
 * Apply theme to document
 */
const applyTheme = (theme) => {
  const root = document.documentElement;
  root.setAttribute(THEME_ATTRIBUTE, theme);

  // Update meta theme-color for mobile browsers
  const metaThemeColor = document.querySelector('meta[name="theme-color"]');
  if (metaThemeColor) {
    metaThemeColor.setAttribute(
      'content',
      theme === THEMES.DARK ? '#0a0a0a' : '#ffffff'
    );
  }
};

export const ThemeProvider = ({ children, defaultTheme }) => {
  const [theme, setThemeState] = useState(() => defaultTheme || getInitialTheme());
  const [isInitialized, setIsInitialized] = useState(false);

  // Apply theme on mount and when theme changes
  useEffect(() => {
    applyTheme(theme);
    localStorage.setItem(THEME_STORAGE_KEY, theme);
    setIsInitialized(true);
  }, [theme]);

  // Listen for system preference changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleChange = (e) => {
      // Only auto-switch if user hasn't manually set a preference
      const storedTheme = localStorage.getItem(THEME_STORAGE_KEY);
      if (!storedTheme) {
        setThemeState(e.matches ? THEMES.DARK : THEMES.LIGHT);
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const setTheme = useCallback((newTheme) => {
    if (Object.values(THEMES).includes(newTheme)) {
      setThemeState(newTheme);
    }
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeState((prev) => (prev === THEMES.LIGHT ? THEMES.DARK : THEMES.LIGHT));
  }, []);

  const value = {
    theme,
    setTheme,
    toggleTheme,
    isLight: theme === THEMES.LIGHT,
    isDark: theme === THEMES.DARK,
    isInitialized,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

/**
 * Hook to access theme context
 */
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export default ThemeContext;
